// Load common styles
import './css/main.scss';

import { type AppContext, appManager, type AuthParams, httpClient, HttpErrorCode, realmSelectionManager } from '@inpart-io/devtools';
import { type ClientContext, useErrorHelper, useServiceWorkerHelper } from '@inpart-io/devtools-vue';
import { padmeConfig } from '@inpart-io/padme-iap';
import { type AxiosError, type AxiosResponse } from 'axios';
import { Dialog, LoadingBar, Notify, type QAjaxBarProps, Quasar } from 'quasar';
import { createApp, onMounted } from 'vue';

// Keep the order between i18n, store and router
import { i18n } from '@/i18n';
import { router } from '@/router';
import { store } from '@/store';
import { trackConfig, trackOtherConfig } from '@/track/config';

import { ROUTE_DATA } from './constants/routes.constants';
import { AJAX_LOADING_BAR_PROPS } from './constants/ui/loading-bar.constants';
import { NOTIFY_PROPS } from './constants/ui/notify.constants';
import { LegacyComponents } from './iuf-components/index';
import { renderErrorPage } from './pages/error/app-startup-fallback';
import { SESSION_MODULE } from './store/session-module';
import { IS_LOGGED_IN } from './store/session-module/getters';

declare let _APP_VERSION_: string;
declare let _AUTH_CLIENT_ID_: string;
declare let _NO_AUTH_: string;
declare let _WEBCLIENT_NAME_: string;
declare let _WEBAPP_BASE_URL_: string;
declare let _WEBAPP_CONTEXT_: string;
declare let _TRACK_CONTEXT_: string;
declare let _TRACK_WEB_CLIENT_BASE_URL_: string;
declare let _FORCED_REALM_: string;
declare let _SW_NAME_: string;
declare let _USER_ID_: string;

window.appVersion = _APP_VERSION_;
trackOtherConfig.webClientBaseUrl = _TRACK_WEB_CLIENT_BASE_URL_;

void (async () => {
    try {
        padmeConfig.webappBaseUrl = _WEBAPP_BASE_URL_;
        padmeConfig.webappContext = _WEBAPP_CONTEXT_;

        // Initializes the Loading Bar plugin
        const loadingBarFilterRegex = new RegExp(`^${padmeConfig.webappFullUrl}`, 'g');

        const loadingBarConfig: QAjaxBarProps = {
            ...AJAX_LOADING_BAR_PROPS,
            hijackFilter(url: string) {
                return loadingBarFilterRegex.test(url);
            }
        };

        // If the user-id is specified, we must bypass authentication so no authParams
        const authParams: AuthParams =
            _USER_ID_ === undefined
                ? {
                      persistToken: false,
                      clientConfig: {
                          realm: _FORCED_REALM_,
                          clientId: _AUTH_CLIENT_ID_
                      },
                      agentOptions: {}
                  }
                : undefined;

        const appContext: ClientContext & AppContext = {
            authParams,
            appConfig: padmeConfig,
            appVersion: window.appVersion,
            appName: _WEBCLIENT_NAME_,
            swName: _SW_NAME_,
            i18n,
            store,
            router
        };

        // Bootstrap application
        // Because the clientManager no longer works in this mono repo setup, we need to
        // manually create the appContext and pass it to the appManager.
        // This is a temporary solution until the clientManager is fixed OR dropped in favor this approach.
        await appManager.startUp(appContext);

        const trackContext = _TRACK_CONTEXT_;
        if (trackContext) {
            trackConfig.webappBaseUrl = _WEBAPP_BASE_URL_;
            trackConfig.webappContext = trackContext;
        }

        const app = createApp({
            template: '<router-view />',
            setup: () => {
                const errorHelper = useErrorHelper();

                router.onError((error: number) => {
                    console.error(errorHelper.getStandardMessage(error));
                    errorHelper.displayStandardMessage(error);
                });

                // Register service worker only if in prod
                if (appContext.swName !== undefined) {
                    const serviceWorkerHelper = useServiceWorkerHelper();
                    void serviceWorkerHelper.registerSW(appContext.swName);
                }

                LoadingBar.setDefaults(loadingBarConfig);
                Notify.setDefaults(NOTIFY_PROPS);

                // This is a better way to handle the splash screen
                onMounted(() => {
                    console.debug(`App started with version ${window.appVersion} (${app.version})`);
                    setTimeout(() => {
                        document.body.classList.add('loaded');
                    }, 0);
                });
            }
        })
            .use(i18n)
            .use(store)
            .use(router)
            .use(Quasar, { plugins: { Dialog, Notify, LoadingBar } })
            .use(LegacyComponents);

        // Set default runtime error handler
        app.config.errorHandler = (error: unknown) => {
            console.debug(error);
        };

        app.mount('#app');

        if (_NO_AUTH_) {
            const realm = _FORCED_REALM_;
            realmSelectionManager.init(padmeConfig.webappBaseUrl, realm);
            console.warn(realm, padmeConfig.webappBaseUrl);
        }

        const CATCHABLE_ERROR_CODES = [HttpErrorCode.FORBIDDEN, HttpErrorCode.UNAVAILABLE];
        // Interceptor to handle errors
        httpClient.addInterceptorResponse(
            (response: AxiosResponse): AxiosResponse => {
                return response;
            },
            async (error: AxiosError) => {
                const errorCode = error.response?.status ?? HttpErrorCode.UNEXPECTED;

                if (CATCHABLE_ERROR_CODES.includes(errorCode)) {
                    if (errorCode === HttpErrorCode.UNAVAILABLE) {
                        void router.push({ name: ROUTE_DATA.NO_ACCESS_PAGE.name, params: { id: errorCode } });
                    }

                    if (errorCode === HttpErrorCode.FORBIDDEN) {
                        if (store.getters[`${SESSION_MODULE}/${IS_LOGGED_IN}`]) {
                            // User is logged in but does not have access to the requested resource.;
                            void router.push({ name: ROUTE_DATA.ERROR_PAGE.name, params: { id: errorCode } });
                        } else {
                            void router.push({ name: ROUTE_DATA.NO_ACCESS_PAGE.name, params: { id: errorCode } });
                        }
                    }

                    return new Promise(() => {
                        console.error(error);
                    });
                }

                // Propagate the error to the components
                return Promise.reject(error);
            }
        );
    } catch (error) {
        console.error(error);
        renderErrorPage(`Please check that the tenant name in the URL is correct.`);
    }
})();
